import axios from 'axios';
import i18n from '../i18n';
import { DEFAULT_LANGUAGE, FALLBACK_LANGUAGE, normalizeLanguage } from '../i18n/languages';

/**
 * Oyun içeriği adları (eşya, canavar, köy, beceri, NPC) sunucudan Türkçe gelir.
 * Türkçe dışındaki dillerde gameData.json "names" tablosundan çevrilir.
 * Yalnızca tabloda birebir eşleşen adlar değişir; oyuncu adları vb. dokunulmaz.
 */
const NAME_KEYS = ['name', 'itemName', 'monsterName', 'skillName', 'villageName', 'encounterName', 'targetName'];

// Adların istemciden sunucuya geri gönderilip eşleştirildiği uçlar çevrilmez
// (hikâye/NPC eşleşmesi) ve oyuncu adları içeren sosyal uçlar atlanır.
const SKIP_PATHS = ['/api/story', '/api/npcs', '/api/profile', '/api/chat', '/api/leaderboard', '/api/auth', '/api/login', '/api/register'];

let cachedLang = '';
let cachedTable = null;

function nameTable() {
  const lang = i18n.language || 'tr';
  if (lang === cachedLang) return cachedTable;
  cachedLang = lang;
  if (normalizeLanguage(lang) === DEFAULT_LANGUAGE) {
    cachedTable = null;
  } else {
    // Yeni bir dilde eksik ad varsa İngilizcesi gösterilir.
    const fallback = i18n.getResource(FALLBACK_LANGUAGE, 'gameData', 'names') || {};
    const own = i18n.getResource(normalizeLanguage(lang), 'gameData', 'names') || {};
    cachedTable = { ...fallback, ...own };
  }
  return cachedTable;
}

/** Tek bir oyun adını çevirir (Türkçede ve eşleşme yoksa aynen döner). */
export function translateGameName(name) {
  const table = nameTable();
  if (!table || typeof name !== 'string') return name;
  return lookupName(table, name) || name;
}

const COUNT_SUFFIX = /^(.*\S)(\s+x\d+)$/;

/** Tam eşleşme yoksa "Ad x3" gibi adet ekli adları da çevirir. */
function lookupName(table, name) {
  if (table[name]) return table[name];
  const m = COUNT_SUFFIX.exec(name);
  if (m && table[m[1]]) return table[m[1]] + m[2];
  return null;
}

let reverseLang = '';
let reverseTable = null;

/** Çevrilmiş bir adın Türkçe aslını verir (ada göre çalışan oyun kuralları için). */
export function originalGameName(name) {
  const table = nameTable();
  if (!table || typeof name !== 'string') return name;
  if (reverseLang !== cachedLang) {
    reverseLang = cachedLang;
    reverseTable = {};
    Object.keys(table).forEach((tr) => {
      reverseTable[table[tr]] = tr;
    });
  }
  return reverseTable[name] || name;
}

/** Nesne/dizi içindeki ad alanlarını yerinde çevirir. */
export function translateNamesDeep(value, depth = 0) {
  const table = nameTable();
  if (!table || value == null || depth > 8) return value;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) translateNamesDeep(value[i], depth + 1);
    return value;
  }
  if (typeof value === 'object') {
    Object.keys(value).forEach((k) => {
      const v = value[k];
      if (typeof v === 'string') {
        if (NAME_KEYS.includes(k)) {
          const translated = lookupName(table, v);
          if (translated) value[k] = translated;
        }
      } else if (v && typeof v === 'object') {
        translateNamesDeep(v, depth + 1);
      }
    });
  }
  return value;
}

function shouldTranslate(url) {
  if (!url) return false;
  let path = url;
  try {
    path = new URL(url, window.location.origin).pathname;
  } catch {
    /* göreli adres */
  }
  if (!path.startsWith('/api/')) return false;
  return !SKIP_PATHS.some((p) => path.startsWith(p));
}

axios.interceptors.response.use((response) => {
  try {
    if (nameTable() && response?.config?.method === 'get' && shouldTranslate(response.config.url)) {
      translateNamesDeep(response.data);
    }
  } catch {
    /* çeviri hatası yanıtı bozmasın */
  }
  return response;
});
